import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { accountsApi } from '../api/accounts'
import { formatDate, formatMoney } from '../lib/format'

// What the firm has done that is like this account (REQ-080): "what was the last thing that we did that was
// something similar to this?" Won or delivered work only, ranked by likeness — the same organisation, its
// sponsor's portfolio, its industry, its market, its service line — each with the reason in plain words. Read
// from the firm's own record today; an engagement-history feed lands behind the same endpoint later.

export function PriorWorkCard({ spoke, companyId, companyName }: { spoke: string; companyId: string; companyName: string }) {
  const q = useQuery({ queryKey: ['prior-work', spoke, companyId], queryFn: () => accountsApi.priorWork(spoke, companyId), staleTime: 60_000 })
  const items = q.data?.items ?? []
  return (
    <section className="card prior-work">
      <div className="card-head">
        <div>
          <h2>What we have done that is like this</h2>
          <p className="muted">The firm's own delivered work most like {companyName}: the same organisation, its sponsor's portfolio, its industry, its market, its service line. Won or delivered work only — never a pitch.</p>
        </div>
      </div>
      {q.isPending && <p className="muted">Reading the record…</p>}
      {q.isError && <p className="muted">Could not read prior work: {q.error.message}</p>}
      {q.isSuccess && items.length === 0 && <p className="muted">Nothing on file is like this yet. Work the firm has won or is delivering appears here as the pipeline reads it{q.data?.source ? ` (${q.data.source})` : ''}.</p>}
      {items.length > 0 && (
        <ol className="plain prior-work-list">
          {items.map((w) => (
            <li key={w.engagementId}>
              <div className="prior-work-head">
                <Link to={`/opportunities/${encodeURIComponent(w.engagementId)}`}><strong>{w.companyName}</strong></Link>
                {w.serviceLine && <span className="tag">{w.serviceLine}</span>}
                <span className={`tag stage ${w.stage}`}>{w.stage === 'won' ? 'Won' : 'In delivery'}</span>
              </div>
              <span className="muted small">{[w.amount ? formatMoney(w.amount) : null, w.when ? formatDate(w.when) : null].filter(Boolean).join(' · ')}</span>
              <p className="prior-work-why">{w.why}</p>
            </li>
          ))}
        </ol>
      )}
      {q.isSuccess && <p className="muted small prior-work-source">Read from {q.data.source}. The firm's engagement history (Intapp / CRM) joins here when that feed is connected.</p>}
    </section>
  )
}
